import * as React from 'react';

import { enumUnit, LoadStatus, Unit } from '@rematter/frontend-lib-api-client';
import { useLoadLineItemsTotals } from '@rematter/modules/trade/hooks/useLoadLineItemsTotals';
import { MeasuredValue, Money } from '@rematter/ui-web';

import {
  DeleteButtonCell,
  EstimatedPriceFieldCell,
  InventoryTagsFieldCell,
  ItemTypeFieldCell,
  MaterialCell,
  QuantityOwedFieldCell,
  RemainingWeightButtonCell,
  RequestedQuantityFieldCell,
  UnitPriceFieldCell,
} from './fields';
import {
  FilterableOption,
  LineItemErrors,
  RemovedSalesOrderLineItem,
} from './types';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadFormLineItem, WeightUnitType } from '~/modules/trade/types/load';
import { loadHasBeenShipped } from '~/modules/trade/utils/load';
import {
  FooterCell,
  HeaderCell,
  LineItemFooter,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
} from '~/shared/components/line-items';

const GRID_TEMPLATE =
  'grid-cols-[minmax(16rem,2fr)_1fr_1fr_var(--size-input)_1fr_100px_2fr_1fr_var(--size-input)]';

export interface SalesOrderLoadTableProps {
  lineItems: LoadFormLineItem[];
  onUpdateLineItem: (id: string, updates: Partial<LoadFormLineItem>) => void;
  onDeleteLineItem: (id: string) => void;
  onAddLineItem?: () => void;
  onChangeRequestedQuantity: (lineItemId: string, value: number) => void;
  onUseRemainingQuantity?: () => void;
  removedSalesOrderLineItems?: RemovedSalesOrderLineItem[];
  disabled?: boolean;
  hideAddButton?: boolean;
  isLoading?: boolean;
  status?: LoadStatus;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  errors?: Record<string, LineItemErrors>;
}

export const SalesOrderLoadTable: React.FC<SalesOrderLoadTableProps> = ({
  lineItems,
  onUpdateLineItem,
  onDeleteLineItem,
  onAddLineItem,
  onChangeRequestedQuantity,
  onUseRemainingQuantity,
  removedSalesOrderLineItems,
  disabled = false,
  hideAddButton = false,
  isLoading = false,
  status,
  weightUnit = 'price',
  facilityDefaultUnit,
  errors,
}) => {
  const t = useTranslations();
  const isShipped = loadHasBeenShipped(status);

  const totals = useLoadLineItemsTotals({
    lineItems,
    facilityDefaultUnit: facilityDefaultUnit ?? enumUnit.Pounds,
    weightUnit,
  });

  const removedItemOptions: FilterableOption[] = React.useMemo(
    () =>
      removedSalesOrderLineItems?.map((item) => ({
        id: item.itemId,
        label: item.itemName,
      })) ?? [],
    [removedSalesOrderLineItems],
  );

  const isEmpty = lineItems.length === 0;

  return (
    <LineItemTable
      isEmpty={isEmpty}
      onAdd={onAddLineItem}
      hideAddButton={hideAddButton}
      disabled={disabled}
      isLoading={isLoading}
      addButtonLabel={t.itemTable.actions.addMaterial}
      className="@SalesOrderLoadTable"
    >
      <LineItemHeader gridTemplate={GRID_TEMPLATE}>
        <HeaderCell>{t.itemTable.salesOrderLoadHeader.material}</HeaderCell>
        <HeaderCell>{t.itemTable.salesOrderLoadHeader.unitPrice}</HeaderCell>
        <HeaderCell className="col-span-2">
          {isShipped
            ? t.itemTable.salesOrderLoadHeader.netWeight
            : `${t.itemTable.salesOrderLoadHeader.soRemaining} ${t.itemTable.salesOrderLoadHeader.weight}`}
        </HeaderCell>
        <HeaderCell className="flex-col items-start">
          {t.itemTable.salesOrderLoadHeader.requestedWeight}
          {!isShipped && onUseRemainingQuantity && (
            <button
              type="button"
              onClick={disabled ? undefined : onUseRemainingQuantity}
              className="cursor-pointer p-0 text-sm text-primary/60 hover:text-primary/80 hover:underline"
            >
              {t.itemTable.salesOrderLoadHeader.useRemainingWeight}
            </button>
          )}
        </HeaderCell>
        <HeaderCell>{t.itemTable.salesOrderLoadHeader.type}</HeaderCell>
        <HeaderCell>
          {t.itemTable.salesOrderLoadHeader.inventoryTags}
        </HeaderCell>
        <HeaderCell>
          {t.itemTable.salesOrderLoadHeader.estimatedPrice}
        </HeaderCell>
        <HeaderCell />
      </LineItemHeader>

      {lineItems.map((lineItem) => (
        <LineItemRow key={lineItem.id} gridTemplate={GRID_TEMPLATE}>
          <MaterialCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disableMaterialSelection={
              !lineItem.pendingSalesOrderMaterialSelection
            }
            itemOptions={
              lineItem.pendingSalesOrderMaterialSelection
                ? removedItemOptions
                : undefined
            }
            errors={errors?.[lineItem.id]}
          />
          <UnitPriceFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled
          />
          <QuantityOwedFieldCell
            lineItem={lineItem}
            weightUnit={weightUnit}
            facilityDefaultUnit={facilityDefaultUnit}
            disabled
          />
          <RemainingWeightButtonCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            onChangeRequestedQuantity={onChangeRequestedQuantity}
            disabled={disabled}
            isSalesOrderLoad
          />
          <RequestedQuantityFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            onChangeRequestedQuantity={onChangeRequestedQuantity}
            disabled={disabled}
            weightUnit={weightUnit}
            facilityDefaultUnit={facilityDefaultUnit}
          />
          <ItemTypeFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
          />
          <InventoryTagsFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
          />
          <EstimatedPriceFieldCell lineItem={lineItem} disabled />
          <DeleteButtonCell
            lineItem={lineItem}
            onDeleteLineItem={onDeleteLineItem}
            disabled={disabled}
            confirmationType="salesOrderLoad"
          />
        </LineItemRow>
      ))}

      {!isEmpty && (
        <LineItemFooter gridTemplate={GRID_TEMPLATE}>
          <FooterCell className="font-bold text-neutral">
            {totals.itemsCount}{' '}
            {t.itemTable.salesOrderLoadFooter.material(totals.itemsCount)}
          </FooterCell>
          <FooterCell />
          <FooterCell />
          <FooterCell />
          <FooterCell className="flex-col items-end gap-1 font-bold">
            {!!totals.requestedWeight && (
              <div>
                <MeasuredValue
                  value={totals.requestedWeight.value}
                  unit={totals.requestedWeight.unit}
                />
              </div>
            )}
            {!!totals.requestedEachCount && (
              <div>
                <MeasuredValue
                  value={totals.requestedEachCount}
                  unit={enumUnit.Each}
                />
              </div>
            )}
          </FooterCell>
          <FooterCell />
          <FooterCell />
          <FooterCell className="justify-between font-bold">
            <Money amount={totals.totalEstimatedPrice} />
          </FooterCell>
          <FooterCell />
        </LineItemFooter>
      )}
    </LineItemTable>
  );
};
